import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { Trans, useTranslation } from 'react-i18next';
import ClockIconSrc from '../../../assets/svg/clock.svg';

import { theme } from '../../../assets/styles/theme';
import { dateUtils } from '../../../utils/dateUtils';
import { AuctionDto } from '../../../services/Auction/auctionService.dto';
import { TextMain } from '../../../components/typography/Texts';
import { getAuctionEndStatus } from '../utils/getAuctionEndStatus';

const Container = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  align-items: center;
`;

const IconWrapper = styled.div`
  justify-content: center;
  width: 24px;
`;

const RedText = styled(TextMain).attrs({ as: 'span' })`
  color: ${theme.color.red};
`;

const GreenText = styled(TextMain).attrs({ as: 'span' })`
  color: ${theme.color.lightGreen};
`;

const HorizontalText = styled.div`
  display: flex;
  flex-direction: row;
  gap: 5px;
`;

const ClockIcon = styled.img`
  width: 18px;
  height: 18px;
`;

interface Props {
  auction: AuctionDto;
}

const AuctionEndInfo = ({ auction }: Props) => {
  const { t } = useTranslation('onsite');

  const [currentDateTime, setCurrentDateTime] = useState(dateUtils.currentDate());

  useEffect(() => {
    if (auction) {
      const interval = setInterval(() => setCurrentDateTime(dateUtils.currentDate()), 1000);
      return () => clearInterval(interval);
    }
  }, [auction]);

  const renderEndDateTime = () => {
    const endDateTime = dateUtils.parseBackendDate(auction.endDateTime);
    const { status, endInfo } = getAuctionEndStatus(auction.status, currentDateTime, endDateTime);

    if (status === 'ENDED') {
      return <RedText>{t('auction.auction-ended')}</RedText>;
    } else if (status === 'ENDING_SOON') {
      return (
        <TextMain>
          <Trans i18nKey='onsite:auction.auction-ends-in' values={{ end: endInfo }} components={[<GreenText />]} />
        </TextMain>
      );
    } else {
      return <TextMain>{t('auction.auction-ends-on', { end: endInfo })}</TextMain>;
    }
  };

  if (!auction) return null;

  return (
    <Container>
      <IconWrapper>
        <ClockIcon src={ClockIconSrc} />
      </IconWrapper>
      <HorizontalText>{renderEndDateTime()}</HorizontalText>
    </Container>
  );
};

export default AuctionEndInfo;
